import React, { useEffect, useRef } from "react";
import './style.css';
import personal from '../../assets/personal.jpeg';

const SECTIONS = [
  {
    id: 1,
    headline: "building products people actually use",
    break: "",
    sub: "working between product and engineering, bringing ideas together with the people and technology needed to build them.",
    bg: "#1b1b1b",
    textColor: "#fff",
    accentColor: "#166544",
    personal: personal,
  },
  {
    id: 2,
    headline: "engineering, formally.",
    sub: "earned an engineering degree the traditional way, years of math, logic, and learning how to turn complex problems into structured solutions.",
    bg: "#166544",
    textColor: "#fff",
    accentColor: "#e99abb",
  },
  {
    id: 3,
    headline: "solutions, not just ideas",
    sub: "my goal is to create solutions that are simple, effective, and thoughtfully designed through a touch of creativity.",
    bg: "#e99abb",
    textColor: "#141414",
    accentColor: "#166544",
  },
];

export default function Scroll() {
  const panelRefs = useRef([]);
  const contentRefs = useRef([]);
  const trackRefs = useRef([]);

  useEffect(() => {
    let rafId;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.25 }
    );

    contentRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    const onFrame = () => {
      const scrollY = window.scrollY;
      const vh = window.innerHeight;

      SECTIONS.forEach((_, i) => {
        const track = trackRefs.current[i];
        const panel = panelRefs.current[i];
        if (!track || !panel) return;

        const trackTop = track.offsetTop;
        const exitStart = trackTop + vh * 0.5;
        const exitEnd = trackTop + vh;

        const progress = Math.min(
          1,
          Math.max(0, (scrollY - exitStart) / (exitEnd - exitStart))
        );

        if (i < SECTIONS.length - 1) {
          const scale = 1 - progress * 0.1;
          const opacity = 1 - progress * 0.6;
          panel.style.transform = `scale(${scale})`;
          panel.style.opacity = String(opacity);
        }
      });

      rafId = requestAnimationFrame(onFrame);
    };

    rafId = requestAnimationFrame(onFrame);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  const splitWords = (text) =>
    text.split(/\s+/).map((word, idx) => (
      <span
        key={idx}
        className="wave-char"
        style={{
          "--char-index": idx,
          display: "inline-block",
          whiteSpace: "nowrap",
          marginRight: "0.25em",
          marginBottom: "0.12em"
        }}
      >
        {word}
      </span>
    ));

  return (
    <div className="scroll-stack-root">
      {SECTIONS.map((section, i) => (
        <div
          key={section.id}
          ref={(el) => (trackRefs.current[i] = el)}
          style={{
            position: "relative",
            height: i === SECTIONS.length - 1 ? "100vh" : "200vh",
            zIndex: (i + 1) * 10,
          }}
        >
          <div
            ref={(el) => (panelRefs.current[i] = el)}
            style={{
              position: "sticky",
              top: 0,
              height: "100vh",
              width: "100%",
              overflow: "hidden",
              transformOrigin: "center top",
              transition: "transform 0.05s linear, opacity 0.05s linear",
              willChange: "transform, opacity",
              borderRadius: i > 0 ? "24px 24px 0 0" : "0",
              backgroundColor: section.bg,
            }}
          >
            <img
              src=""
              alt=""
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: 0.45,
                objectPosition: "bottom"
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(135deg, ${section.bg} 30%, transparent 100%)`,
              }}
            />

            <div
              ref={(el) => (contentRefs.current[i] = el)}
              className="section-content-enter"
              style={{
                position: "relative",
                zIndex: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: i === 0 ? "center" : "flex-start",
                textAlign: i === 0 ? "center" : "left",

                margin: i === 0 ? "0 auto" : "0",
                maxWidth: i === 0 ? "700px" : "900px",

                padding: "clamp(2rem, 8vw, 8rem)",
                color: section.textColor,
                paddingTop: "0px"
              }}
            >
              <span
                className="wave-text"
                style={{
                  color: section.accentColor,
                  borderColor: section.accentColor,
                  marginBottom: "2rem",
                  display: "inline-block",
                  overflow: "hidden",
                }}
              >
                {splitWords(section.label || "")}
              </span>

              <h2
                style={{
                  fontSize: "60px",
                  fontWeight: 900,
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  marginBottom: "2rem",
                  whiteSpace: "pre-line",
                  color: section.textColor,
                  display: "inline-block",
                  overflow: "hidden",
                }}
                className="wave-text"
              >
                {splitWords(section.headline)} <br/>
                {splitWords(section.break || "")}
              </h2>

              <div
                style={{
                  width: "60px",
                  height: "2px",
                  background: section.accentColor,
                  marginBottom: "1.5rem",
                  borderRadius: "1px",
                }}
              />
              <p
                style={{
                  fontSize: "clamp(1rem, 1.5vw, 1.25rem)",
                  lineHeight: 1.7,
                  opacity: 0.8,
                  maxWidth: "100%",
                  fontWeight: 300,
                  display: "inline-block",
                  overflow: "hidden",
                  wordBreak: "normal",
                }}
                className="wave-text"
              >
                {splitWords(section.sub)}
              </p>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1.25rem",
                  padding: "1.25rem 1.75rem",
                  border: `1px solid ${section.borderColor}`,
                  borderRadius: "12px",
                  width: "fit-content",
                  backdropFilter: "blur(8px)",
                  background: `${section.bg}33`,
                }}
              >
                <span
                  className="wave-text"
                  style={{
                    fontSize: "2.5rem",
                    fontWeight: 700,
                    color: section.accentColor,
                    lineHeight: 1,
                    display: "inline-block",
                    overflow: "hidden",
                  }}
                >
                  {splitWords(section.stat || "")}
                </span>
                <span
                  className="wave-text"
                  style={{
                    fontSize: "0.8rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.15em",
                    opacity: 0.6,
                    fontWeight: 500,
                    display: "inline-block",
                    overflow: "hidden",
                  }}
                >
                  {splitWords(section.statLabel || "")}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}

      <style>{`
        .section-content-enter {
          opacity: 0;
          transform: translateY(50px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
        .section-content-enter.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(6px); }
        }

       .wave-text .wave-char {
        display: inline-block;
        opacity: 0;
        transform: translateY(30px); 
        transition: transform 0.45s cubic-bezier(.22,.8,.25,1),
                    opacity 0.45s ease;
        transition-delay: calc(0.15s + var(--char-index) * 0.03s);
      }

        .section-content-enter.is-visible .wave-text .wave-char {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}